import React, { useContext, useEffect, useRef } from "react";
import { Box, Typography, Button, IconButton, Icon } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { customEvent } from "src/utils/gtag.js";
import { useCountdown } from "src/utils/useCountdown.js";
import CenterDialog from "src/components/blocks/dialogs/CenterDialog.js";

export const OFFER_DISCOUNT_PERCENT = 20;
const BASE_PRICE = "$2.50";
const DISCOUNTED_PRICE = "$2.00"; // $2.50 - 20%
const BENEFIT_LINE = "Unlimited calendars, always up to date.";

/**
 * Limited-time-offer modal with a live HH:MM:SS countdown to `deadline`.
 * Subscribe routes into Stanza+ checkout carrying `discount` in navigation
 * state; once the countdown expires the offer swaps for a full-price
 * "See plans" CTA rather than disabling.
 */
export const OfferModal = ({ open, onClose, deadline }) => {
  const { user } = useContext(AuthContext) ?? {};
  const navigate = useNavigate();
  // Tick only while visible — the pill already runs its own countdown.
  const { formatted, expired } = useCountdown(open ? deadline : null);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (open && !viewedRef.current) {
      viewedRef.current = true;
      customEvent({
        name: "offer_modal_viewed",
        category: "Offer",
        label: "Offer Modal Viewed",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          discount: OFFER_DISCOUNT_PERCENT,
        },
      });
    }
    if (!open) {
      viewedRef.current = false;
    }
  }, [open, user]);

  const handleSubscribe = () => {
    customEvent({
      name: "offer_subscribe_clicked",
      category: "Offer",
      label: "Offer Subscribe Clicked",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        discount: OFFER_DISCOUNT_PERCENT,
      },
    });
    onClose?.();
    navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
      state: { source: "offer_modal", discount: OFFER_DISCOUNT_PERCENT },
    });
  };

  const handleMaybeLater = () => {
    onClose?.();
  };

  const handleSeePlans = () => {
    onClose?.();
    navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
      state: { source: "offer_modal_expired" },
    });
  };

  return (
    <CenterDialog
      open={open}
      handleClose={onClose}
      maxWidth="xs"
      fullWidth
      hideCloseButton
      data-testid="offer-modal"
      sx={{ ".MuiDialog-paper": { borderRadius: "24px", p: 1 } }}
    >
      <IconButton
        onClick={onClose}
        data-testid="offer-modal-close"
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 30 }}
        aria-label="close"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box sx={{ textAlign: "center", pt: 2 }}>
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{ fontSize: "2.5rem", mb: 1, fontWeight: 300 }}
        >
          bolt
        </Icon>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {expired ? "This offer has ended." : `${OFFER_DISCOUNT_PERCENT}% off Stanza+`}
        </Typography>

        {!expired && (
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary", textWrap: "balance" }}>
            {BENEFIT_LINE}
          </Typography>
        )}

        {!expired && (
          <>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "baseline",
                gap: 1,
                mb: 1,
              }}
            >
              <Typography
                component="span"
                data-testid="offer-base-price"
                sx={{ textDecoration: "line-through", color: "text.secondary" }}
              >
                {BASE_PRICE}
              </Typography>
              <Typography
                component="span"
                data-testid="offer-discounted-price"
                sx={{ fontWeight: 800, fontSize: "1.5rem" }}
              >
                {DISCOUNTED_PRICE}/mo
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 2 }}>
              Billed annually · cancel anytime
            </Typography>

            <Box
              data-testid="offer-modal-countdown"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "success.main",
                color: "success.main",
                borderRadius: "16px",
                px: 2,
                py: 0.75,
                mb: 3,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "0.5px",
              }}
            >
              Offer ends in · {formatted}
            </Box>
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={expired ? handleSeePlans : handleSubscribe}
          data-testid={expired ? "offer-see-plans-button" : "offer-subscribe-button"}
          sx={{ borderRadius: "25px", py: 1.25, mb: 1, fontWeight: 700 }}
        >
          {expired ? "See plans" : `Get ${OFFER_DISCOUNT_PERCENT}% off`}
        </Button>
        <Button
          variant="text"
          fullWidth
          onClick={handleMaybeLater}
          data-testid="offer-maybe-later-button"
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Maybe later
        </Button>
      </Box>
    </CenterDialog>
  );
};

export default OfferModal;
